import React, { useState } from 'react';
import { View, Modal, TextInput, TouchableOpacity, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Alert } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { CustomPicker } from '../common/CustomPicker';
import { BusinessProfile, BusinessFormData, BUSINESS_CATEGORIES } from '../../types/profile';
import ThemedText from '../common/ThemedText';

interface EditBusinessModalProps {
  visible: boolean;
  business?: BusinessProfile;
  onClose: () => void;
  onSave: (data: BusinessFormData) => void;
}

const EditBusinessModal: React.FC<EditBusinessModalProps> = ({
  visible,
  business,
  onClose,
  onSave,
}) => {
  const insets = useSafeAreaInsets();
  const [formData, setFormData] = useState<BusinessFormData>({
    name: business?.name || '',
    category: business?.category || 'Technology',
    description: business?.description || '',
    city: business?.city || '',
    yearsOfExperience: business?.yearsOfExperience,
    website: business?.website || '',
    contactEmail: business?.contactEmail || '',
  });

  const handleSave = () => {
    // Validate required fields
    if (!formData.name.trim()) {
      Alert.alert('Error', 'Business name is required');
      return;
    }
    if (!formData.description.trim()) {
      Alert.alert('Error', 'Business description is required');
      return;
    }

    const dataToSave: BusinessFormData = {
      name: formData.name.trim(),
      category: formData.category,
      description: formData.description.trim(),
      city: formData.city?.trim() || undefined,
      yearsOfExperience: formData.yearsOfExperience,
      website: formData.website?.trim() || undefined,
      contactEmail: formData.contactEmail?.trim() || undefined,
    };

    onSave(dataToSave);
    onClose();
  };

  const isEditMode = !!business;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={[styles.header, { paddingTop: 16 + insets.top }]}>
          <TouchableOpacity onPress={onClose} style={styles.headerButton}>
            <Ionicons name="close" size={28} color="#000000" />
          </TouchableOpacity>
          <ThemedText style={styles.headerTitle}>
            {isEditMode ? 'Edit Business' : 'Create Business'}
          </ThemedText>
          <TouchableOpacity onPress={handleSave} style={styles.headerButton}>
            <ThemedText style={styles.saveText}>Save</ThemedText>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.section}>
            <ThemedText style={styles.sectionTitle}>Business Information</ThemedText>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Business Name *</ThemedText>
              <TextInput
                style={styles.input}
                value={formData.name}
                onChangeText={(text) => setFormData({ ...formData, name: text })}
                placeholder="Enter business name"
                maxLength={100}
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Category *</ThemedText>
              <CustomPicker
                selectedValue={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value as string })}
                items={BUSINESS_CATEGORIES.map((category) => ({
                  label: category,
                  value: category,
                }))}
                placeholder="Select category"
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Description *</ThemedText>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={formData.description}
                onChangeText={(text) => setFormData({ ...formData, description: text })}
                placeholder="Describe your business and services"
                multiline
                numberOfLines={4}
                maxLength={500}
                textAlignVertical="top"
              />
              <ThemedText style={styles.charCount}>{formData.description.length}/500</ThemedText>
            </View>
          </View>

          <View style={styles.section}>
            <ThemedText style={styles.sectionTitle}>Additional Details (Optional)</ThemedText>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Business City</ThemedText>
              <TextInput
                style={styles.input}
                value={formData.city}
                onChangeText={(text) => setFormData({ ...formData, city: text })}
                placeholder="e.g., San Francisco"
                maxLength={100}
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Years of Experience</ThemedText>
              <TextInput
                style={styles.input}
                value={formData.yearsOfExperience?.toString() || ''}
                onChangeText={(text) => {
                  const num = parseInt(text);
                  setFormData({
                    ...formData,
                    yearsOfExperience: isNaN(num) ? undefined : num,
                  });
                }}
                placeholder="Number of years"
                keyboardType="number-pad"
                maxLength={2}
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Website</ThemedText>
              <TextInput
                style={styles.input}
                value={formData.website}
                onChangeText={(text) => setFormData({ ...formData, website: text })}
                placeholder="https://yourbusiness.com"
                keyboardType="url"
                autoCapitalize="none"
                maxLength={200}
              />
            </View>

            <View style={styles.fieldContainer}>
              <ThemedText style={styles.label}>Contact Email</ThemedText>
              <TextInput
                style={styles.input}
                value={formData.contactEmail}
                onChangeText={(text) => setFormData({ ...formData, contactEmail: text })}
                placeholder="contact@yourbusiness.com"
                keyboardType="email-address"
                autoCapitalize="none"
                maxLength={100}
              />
            </View>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F2EF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  headerButton: {
    width: 60,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#000000',
  },
  saveText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0A66C2',
    textAlign: 'right',
  },
  content: {
    flex: 1,
  },
  section: {
    backgroundColor: '#FFFFFF',
    marginTop: 16,
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#000000',
    marginBottom: 16,
  },
  fieldContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 15,
    color: '#000000',
  },
  textArea: {
    height: 100,
    paddingTop: 12,
  },
  pickerContainer: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 6,
    overflow: 'hidden',
  },
  picker: {
    height: 50,
  },
  charCount: {
    fontSize: 12,
    color: '#999999',
    textAlign: 'right',
    marginTop: 4,
  },
  bottomPadding: {
    height: 40,
  },
});

export default EditBusinessModal;
